/**
 * `OrcaRunnerRpcs` — the runner channel.
 *
 * The RPC group a runner daemon uses to join the pool and drain its command
 * stream. Every endpoint is authenticated by {@link OrcaRunnerMiddleware}
 * (runner bearer token, keyed on `orca_runners`):
 *
 *   - `register` (unary) — protocol/spec version handshake. The daemon reports
 *     the wire versions it speaks plus its self-declared capacity/labels; the
 *     server validates compatibility, marks the runner ONLINE, and echoes back
 *     the server's versions + the resolved `runnerId`.
 *   - `connect` (stream) — drains the durable per-runner Redis command stream
 *     (I15) via `XREAD BLOCK`, resuming from the daemon's `lastSeenId` (or the
 *     server-persisted cursor when omitted). Emits {@link RunnerCommandDelivery}
 *     envelopes carrying the Redis stream id alongside each command.
 *   - `ack` (unary) — confirms a delivered command (`commandId` + optional
 *     `containerId` + outcome) and advances the durable cursor to its
 *     `streamId`, so a reconnecting daemon resumes past acknowledged work.
 *   - `reportContainers` (unary) — periodic full inventory; records liveness.
 *   - `heartbeat` (unary) — lightweight liveness ping.
 *   - `credentials.redeemLease` (unary) — spends the one-time capability the
 *     orchestrator put on `ContainerSpec.credentialRef` and returns the
 *     decrypted credential set for the run's actor. The SINGLE decrypt point;
 *     the runner id is taken from the auth context, never the payload.
 *
 * The command/ack/report/heartbeat payload schemas are the shared protocol
 * types from `orca-runner.schemas.ts` (also imported by the daemon and the
 * orchestrator runtime). The handshake types and constants live here because
 * they are specific to this RPC group.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import { DatabaseError, ValkeyError } from "./errors.ts";
import {
  CommandAck,
  CredentialKind,
  HeartbeatPayload,
  ReportContainersPayload,
  RunnerCommand,
} from "./orca-runner.schemas.ts";
import { OrcaRunnerMiddleware, OrcaRunnerUnauthorized } from "./orca-runner.tags.ts";

// ============================================================================
// Versioning
// ============================================================================

/**
 * Wire protocol version the orchestrator speaks. A daemon must `register` with
 * the exact same value or the handshake fails with {@link RunnerVersionMismatch}.
 *
 * @since 1.0.0
 */
export const ORCA_RUNNER_PROTOCOL_VERSION = 1;

/**
 * {@link ContainerSpec} schema version the orchestrator emits. A daemon must
 * `register` with the exact same value or the handshake fails with
 * {@link RunnerVersionMismatch}.
 *
 * @since 1.0.0
 */
export const ORCA_RUNNER_SPEC_VERSION = 1;

// ============================================================================
// register
// ============================================================================

/**
 * Handshake payload. `protocolVersion` / `specVersion` are the wire versions
 * the daemon speaks; `capacity` (max concurrent tasks) and `labels`
 * (scheduling tags) are the runner's self-declared configuration, persisted on
 * the `orca_runners` row.
 *
 * @since 1.0.0
 */
export const RegisterPayload = Schema.Struct({
  protocolVersion: Schema.Number,
  specVersion: Schema.Number,
  capacity: Schema.optionalWith(Schema.Number, { default: () => 1 }),
  labels: Schema.optionalWith(Schema.Array(Schema.String), { default: () => [] }),
});

/**
 * @since 1.0.0
 */
export type RegisterPayload = Schema.Schema.Type<typeof RegisterPayload>;

/**
 * Handshake response. Carries the server's wire versions and the `runnerId`
 * the bearer token resolved to, so the daemon can tag its later RPCs.
 *
 * @since 1.0.0
 */
export const RegisterResponse = Schema.Struct({
  runnerId: Schema.String,
  protocolVersion: Schema.Number,
  specVersion: Schema.Number,
});

/**
 * @since 1.0.0
 */
export type RegisterResponse = Schema.Schema.Type<typeof RegisterResponse>;

/**
 * Returned by `register` when the daemon's protocol or spec version does not
 * match the server's. `field` says which one diverged; `expected` is the
 * server's value, `received` the daemon's.
 *
 * @since 1.0.0
 */
export class RunnerVersionMismatch extends Schema.TaggedError<RunnerVersionMismatch>()(
  "RunnerVersionMismatch",
  {
    field: Schema.Literal("protocol", "spec"),
    expected: Schema.Number,
    received: Schema.Number,
    message: Schema.String,
  },
) {}

/**
 * Returned by `credentials.redeemLease` when the presented capability cannot be
 * spent. `reason` distinguishes the three refusals for diagnostics only —
 * `message` is GENERIC by contract and names no credential, lease, person or
 * customer id, because the caller holding an unredeemable token has not proven
 * it may learn anything about the lease it guessed at.
 *
 *  - `invalid` — no lease matches the presented digest (or the lease names a
 *    credential its owner no longer holds);
 *  - `expired` — the lease existed but its 120-second window has closed;
 *  - `consumed` — the lease was already spent, by this runner or another. Of
 *    two concurrent redemptions of one token exactly one wins; the loser sees
 *    this.
 *
 * @since 1.0.0
 */
export class CredentialLeaseError extends Schema.TaggedError<CredentialLeaseError>()(
  "CredentialLeaseError",
  {
    reason: Schema.Literal("invalid", "expired", "consumed"),
    message: Schema.String,
  },
) {}

// ============================================================================
// connect + ack
// ============================================================================

/**
 * `connect` payload. `lastSeenId` is the Redis stream id the daemon last
 * processed; when omitted the server resumes from its persisted cursor
 * (defaulting to the start of the stream for a fresh runner).
 *
 * @since 1.0.0
 */
export const ConnectPayload = Schema.Struct({
  lastSeenId: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export type ConnectPayload = Schema.Schema.Type<typeof ConnectPayload>;

/**
 * A single command delivered over `connect`. `streamId` is the Redis entry id
 * the daemon echoes back in its {@link RunnerAck} to advance the durable
 * cursor; `command` is the decoded {@link RunnerCommand}.
 *
 * @since 1.0.0
 */
export const RunnerCommandDelivery = Schema.Struct({
  streamId: Schema.String,
  command: RunnerCommand,
});

/**
 * @since 1.0.0
 */
export type RunnerCommandDelivery = Schema.Schema.Type<typeof RunnerCommandDelivery>;

/**
 * A liveness keepalive frame emitted on the `connect` stream during idle windows
 * (no commands pending). It carries no command — the daemon skips it without
 * dispatching or acking — and exists for two reasons:
 *
 *   1. **Keep the wire warm.** The `connect` response is a single long-lived
 *      NDJSON body. With no commands the server would otherwise send zero bytes
 *      for minutes, so any idle/buffer/keepalive timeout in an intermediary
 *      (ngrok edge, a dev proxy) silently resets the stream (~5min cadence). A
 *      periodic keepalive frame keeps bytes flowing so the stream survives.
 *   2. **Let the daemon detect a dead connection.** A half-open TCP socket
 *      delivers neither commands nor keepalives; the daemon's stall timeout
 *      (see `RunnerRpcClient`) observes the keepalive gap and reconnects.
 *
 * `sentAt` is when the server emitted the frame.
 *
 * @since 1.0.0
 */
export const RunnerKeepalive = Schema.TaggedStruct("RunnerKeepalive", {
  sentAt: Schema.DateTimeUtc,
});

/**
 * @since 1.0.0
 */
export type RunnerKeepalive = Schema.Schema.Type<typeof RunnerKeepalive>;

/**
 * A frame delivered on the `connect` stream: either a real
 * {@link RunnerCommandDelivery} (execute + ack) or a {@link RunnerKeepalive}
 * (idle liveness ping the daemon skips). The two members are structurally
 * distinct — only the keepalive carries a `_tag` — so {@link isRunnerKeepalive}
 * discriminates them.
 *
 * @since 1.0.0
 */
export const RunnerStreamFrame = Schema.Union(RunnerCommandDelivery, RunnerKeepalive);

/**
 * @since 1.0.0
 */
export type RunnerStreamFrame = Schema.Schema.Type<typeof RunnerStreamFrame>;

/**
 * Whether a {@link RunnerStreamFrame} is a {@link RunnerKeepalive} (vs a real
 * command delivery). The daemon uses this to skip keepalives without acking —
 * acking a keepalive (which carries no `streamId`) would corrupt the durable
 * cursor.
 *
 * @since 1.0.0
 */
export const isRunnerKeepalive = (frame: RunnerStreamFrame): frame is RunnerKeepalive =>
  "_tag" in frame && frame._tag === "RunnerKeepalive";

/**
 * `ack` payload — a {@link CommandAck} (commandId + optional containerId +
 * outcome) plus the `streamId` of the delivering entry, which the server
 * persists as the runner's durable cursor.
 *
 * @since 1.0.0
 */
export const RunnerAck = Schema.Struct({
  ...CommandAck.fields,
  streamId: Schema.String,
});

/**
 * @since 1.0.0
 */
export type RunnerAck = Schema.Schema.Type<typeof RunnerAck>;

/**
 * Acknowledgement response for `ack`.
 *
 * @since 1.0.0
 */
export const AckResponse = Schema.Struct({ ok: Schema.Boolean });

/**
 * @since 1.0.0
 */
export type AckResponse = Schema.Schema.Type<typeof AckResponse>;

// ============================================================================
// reportContainers + heartbeat
// ============================================================================

/**
 * Acknowledgement response for `reportContainers`. `count` echoes how many
 * running containers the daemon reported.
 *
 * @since 1.0.0
 */
export const ReportContainersResponse = Schema.Struct({
  ok: Schema.Boolean,
  count: Schema.Number,
});

/**
 * @since 1.0.0
 */
export type ReportContainersResponse = Schema.Schema.Type<typeof ReportContainersResponse>;

/**
 * Acknowledgement response for `heartbeat`.
 *
 * @since 1.0.0
 */
export const HeartbeatResponse = Schema.Struct({ ok: Schema.Boolean });

/**
 * @since 1.0.0
 */
export type HeartbeatResponse = Schema.Schema.Type<typeof HeartbeatResponse>;

// ============================================================================
// credentials.redeemLease
// ============================================================================

/**
 * `credentials.redeemLease` payload: the one-time capability the daemon took
 * off `ContainerSpec.credentialRef`. `Redacted` for the same reason it is on
 * the spec — possession IS authorization, so it must never reach a log line.
 * The runner id is NOT in the payload and never will be: the handler reads it
 * from {@link OrcaRunnerAuth}, so a daemon cannot stamp someone else's id on
 * the lease it spends.
 *
 * @since 1.0.0
 */
export const RedeemLeasePayload = Schema.Struct({
  leaseToken: Schema.Redacted(Schema.String),
});

/**
 * @since 1.0.0
 */
export type RedeemLeasePayload = Schema.Schema.Type<typeof RedeemLeasePayload>;

/**
 * One decrypted credential in a {@link RedeemLeaseResponse}. `values` is the
 * field map for `kind` (`accessToken`, plus `secretAccessKey` / `region` for
 * `aws_access_key`), which the daemon turns into env names via
 * `credentialEnvForKind`. Every value is `Redacted`.
 *
 * @since 1.0.0
 */
export const RedeemedCredential = Schema.Struct({
  kind: CredentialKind,
  values: Schema.Record({ key: Schema.String, value: Schema.Redacted(Schema.String) }),
});

/**
 * @since 1.0.0
 */
export type RedeemedCredential = Schema.Schema.Type<typeof RedeemedCredential>;

/**
 * `credentials.redeemLease` response: the leased agent-CLI credential plus THE
 * SAME PERSON's active supplementary credentials (`github_token`,
 * `linear_api_key`, `aws_access_key`). One lease, one identity, the whole set —
 * which is why `ContainerSpec.credentialRef` stays singular.
 *
 * @since 1.0.0
 */
export const RedeemLeaseResponse = Schema.Struct({
  entries: Schema.Array(RedeemedCredential),
});

/**
 * @since 1.0.0
 */
export type RedeemLeaseResponse = Schema.Schema.Type<typeof RedeemLeaseResponse>;

// ============================================================================
// RPC group
// ============================================================================

/**
 * Orca runner RPC group. Tags are namespaced under `orca/runner/`. Every
 * endpoint requires {@link OrcaRunnerMiddleware}.
 *
 * @since 1.0.0
 */
export class OrcaRunnerRpcs extends RpcGroup.make(
  Rpc.make("register", {
    payload: RegisterPayload,
    success: RegisterResponse,
    error: Schema.Union(OrcaRunnerUnauthorized, RunnerVersionMismatch, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("connect", {
    payload: ConnectPayload,
    success: RunnerStreamFrame,
    error: Schema.Union(OrcaRunnerUnauthorized, ValkeyError),
    stream: true,
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("ack", {
    payload: RunnerAck,
    success: AckResponse,
    error: Schema.Union(OrcaRunnerUnauthorized, ValkeyError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("reportContainers", {
    payload: ReportContainersPayload,
    success: ReportContainersResponse,
    error: Schema.Union(OrcaRunnerUnauthorized, DatabaseError, ValkeyError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("heartbeat", {
    payload: HeartbeatPayload,
    success: HeartbeatResponse,
    error: Schema.Union(OrcaRunnerUnauthorized, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("credentials.redeemLease", {
    payload: RedeemLeasePayload,
    success: RedeemLeaseResponse,
    error: Schema.Union(OrcaRunnerUnauthorized, CredentialLeaseError, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
).prefix("orca/runner/") {}
